import { ExternalLink, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  SIDEBAR_NAV_ROW_CLASSES,
  SIDEBAR_NAV_ROW_IDLE_CLASSES,
} from '@/shared/components/Layout/SidebarNav/sidebar-nav.tokens';
import { cn } from '@/shared/utils/cn/cn.util';

import { type SidebarFootProps } from './SidebarFoot.types';
import { useSidebarFootLogic } from './useSidebarFootLogic';

const ROW_CLASSES = cn(SIDEBAR_NAV_ROW_CLASSES, SIDEBAR_NAV_ROW_IDLE_CLASSES);

/**
 * The rail's foot — the deployment's company, support address and links (P28).
 *
 * Rows wear the nav row's own box so the rail reads as one list, and the
 * address and every url are pinned LTR: a `mailto` or a domain inside an Arabic
 * column is a bidi trap (P23c1's IBAN, again).
 */
export function SidebarFoot({ isCollapsed }: SidebarFootProps) {
  const { t } = useTranslation('common');
  const { isShown, legalEntityName, supportHref, supportEmail, links } = useSidebarFootLogic({
    isCollapsed,
  });

  if (!isShown) {
    return null;
  }

  return (
    <nav
      aria-label={t('shell.about')}
      className="mt-auto flex flex-col gap-xs border-t border-border pt-md"
    >
      {legalEntityName === '' ? null : (
        <p className="truncate px-md text-caption text-muted-foreground" title={legalEntityName}>
          {legalEntityName}
        </p>
      )}

      {supportHref === null ? null : (
        <a href={supportHref} className={ROW_CLASSES} title={supportEmail}>
          <Mail aria-hidden className="h-4 w-4 shrink-0" />
          <span className="truncate" dir="ltr">
            {supportEmail}
          </span>
        </a>
      )}

      {links.map((link) => (
        <a
          key={link.label}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className={ROW_CLASSES}
          title={link.href}
        >
          <ExternalLink aria-hidden className="h-4 w-4 shrink-0" />
          <span className="truncate">{link.label}</span>
        </a>
      ))}
    </nav>
  );
}
